import { useState } from "react";
import { toast } from "sonner";
import { Save, X } from "lucide-react";
import api, { errText } from "@/lib/api";

const FIELDS = [
  ["name", "Event name", "text", true],
  ["date", "Date", "date", true],
  ["start_time", "Start time", "time", false],
  ["end_time", "End time", "time", false],
  ["venue", "Venue", "text", false],
  ["host_org", "Hosted by (organisation shown to attendees)", "text", false],
  ["host_contact_email", "Organisation contact email (shown in emails)", "email", false],
  ["cc_email", "CC every email to (organiser copy)", "email", false],
  ["forward_email", "Send guest emails to this address instead (client forwards them on)", "email", false],
  ["capacity", "Capacity", "number", false],
];

export const EventForm = ({ event, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: event.name || "",
    date: event.date || "",
    start_time: event.start_time || "",
    end_time: event.end_time || "",
    venue: event.venue || "",
    host_org: event.host_org || "",
    host_contact_email: event.host_contact_email || "",
    cc_email: event.cc_email || "",
    forward_email: event.forward_email || "",
    forward_enabled: event.forward_enabled ?? false,
    description: event.description || "",
    key_visual_url: event.key_visual_url || "",
    capacity: event.capacity || 100,
    waitlist_enabled: event.waitlist_enabled ?? true,
    status: event.status || "published",
    passes_released: event.passes_released ?? true,
    rsvp_required: event.rsvp_required ?? false,
    auto_email_on_register: event.auto_email_on_register ?? false,
    auto_pass_on_rsvp: event.auto_pass_on_rsvp ?? false,
  });
  const [busy, setBusy] = useState(false);
  const [visualBusy, setVisualBusy] = useState(false);

  const uploadVisual = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setVisualBusy(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/events/${event.event_id}/banner`, body, {
        params: { kind: "key_visual" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, key_visual_url: data.banner_url }));
      toast.success("Event image uploaded");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setVisualBusy(false);
    }
  };

  const clearVisual = async () => {
    setVisualBusy(true);
    try {
      await api.delete(`/events/${event.event_id}/banner`, { params: { kind: "key_visual" } });
      setForm((f) => ({ ...f, key_visual_url: "" }));
      toast.success("Event image removed");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setVisualBusy(false);
    }
  };

  const set = (key, type) => (e) =>
    setForm({ ...form, [key]: type === "number" ? Number(e.target.value) : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/events/${event.event_id}`, form);
      toast.success("Event details updated");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 overflow-y-auto p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="card p-6 max-w-2xl mx-auto my-8"
        data-testid="edit-event-form"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Edit event</h2>
            <p className="text-xs text-slate-400 mt-1">
              The public link <span className="font-mono2">/e/{event.slug}</span> stays the same, so anything
              already shared keeps working.
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="edit-event-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {FIELDS.map(([key, label, type, required]) => (
            <div key={key} className={["name", "venue", "host_org", "host_contact_email", "cc_email"].includes(key) ? "sm:col-span-2" : ""}>
              <label className="label">{label}</label>
              <input
                type={type}
                required={required}
                min={type === "number" ? 1 : undefined}
                className="input"
                data-testid={`edit-${key.replaceAll("_", "-")}-input`}
                value={form[key]}
                onChange={set(key, type)}
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="label">Event image</label>
            <p className="text-[11px] text-slate-500 mt-1">
              Shown across the top of the public event page and on your dashboard. PNG, JPG or WebP
              up to 2MB — about 1600×900px looks best.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <label
                className="font-mono2 text-[11px] px-3 py-1.5 rounded-md border border-[#38475c] text-slate-300 hover:text-[#00e599] hover:border-[#00e599]/50 cursor-pointer transition-colors"
                data-testid="edit-key-visual-upload"
              >
                {visualBusy ? "Working…" : form.key_visual_url ? "Replace image" : "Upload image"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={uploadVisual}
                  disabled={visualBusy}
                />
              </label>
              {form.key_visual_url && (
                <button
                  type="button"
                  onClick={clearVisual}
                  disabled={visualBusy}
                  className="font-mono2 text-[11px] px-3 py-1.5 rounded-md border border-[#38475c] text-slate-400 hover:text-red-400"
                  data-testid="edit-key-visual-clear"
                >
                  Remove
                </button>
              )}
            </div>
            {form.key_visual_url ? (
              <img
                src={form.key_visual_url}
                alt=""
                className="mt-3 w-full max-h-44 object-cover rounded-lg border border-[#28323f]"
                data-testid="edit-key-visual-thumb"
              />
            ) : (
              <p className="font-mono2 text-[10px] text-slate-600 mt-2">no image yet</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="label">Description</label>
            <textarea
              rows="4"
              className="input"
              data-testid="edit-description-input"
              value={form.description}
              onChange={set("description")}
            />
          </div>
          <div>
            <label className="label">Visibility</label>
            <select className="input" data-testid="edit-status-input" value={form.status} onChange={set("status")}>
              <option value="published">Published (listed publicly)</option>
              <option value="draft">Draft (link only, hidden from the portal)</option>
              <option value="completed">Completed (closed, registration shut)</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="edit-waitlist-toggle"
                checked={form.waitlist_enabled}
                onChange={(e) => setForm({ ...form, waitlist_enabled: e.target.checked })}
              />
              Enable waitlist when full
            </label>
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4 mt-0.5"
                data-testid="edit-auto-email-toggle"
                checked={form.auto_email_on_register}
                onChange={(e) => setForm({ ...form, auto_email_on_register: e.target.checked })}
              />
              <span>
                Email guests automatically when they register
                <span className="block text-slate-500 mt-1">
                  Leave this off to send nothing until you trigger it yourself from the Command
                  Center — guests still see their pass or RSVP page straight after registering.
                </span>
              </span>
            </label>
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4 mt-0.5"
                data-testid="edit-auto-pass-rsvp-toggle"
                checked={form.auto_pass_on_rsvp}
                onChange={(e) => setForm({ ...form, auto_pass_on_rsvp: e.target.checked })}
              />
              <span>
                Email the pass automatically when a guest replies yes
                <span className="block text-slate-500 mt-1">
                  Off by default. Leave it off to release passes yourself later — the guest still
                  gets their confirmation page.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button className="btn-primary" disabled={busy} data-testid="edit-event-save-btn">
            <Save size={15} /> {busy ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="edit-event-cancel-btn">
            Cancel
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-4">
          Attendees already registered keep their pass. If you change the date, time or venue, use{" "}
          <strong>Send reminders</strong> afterwards so everyone gets the update. "Hosted by" appears at the top
          of every email and on the public event page.
        </p>
      </form>
    </div>
  );
};
